export const agency = {
  // Main page titles
  agencyDashboard: 'Dashboard',
  dashboardDescription: 'Monitor your rental business performance and key metrics',
  bookingsManagement: 'Bookings',
  bookingsPageDescription: 'Manage all your rental reservations and customer bookings',
  newBooking: 'New Booking',
  editBooking: 'Edit Booking',
  totalBookings: '{{count}} bookings',
  
  // Root level translations for BookingModal (only unique ones)
  selectCarFirst: 'Select a vehicle first',
  selectDateFirst: 'Select a date first',
  locationsAvailable: 'locations available',
  carNoLocation: 'This vehicle has no assigned location',
  additionalNotesPlaceholder: 'Add any additional notes or special requirements...',
  
  // Root level keys for client modal
  cin: 'CIN',
  
  // Root level keys for booking modal buttons
  createBooking: 'Create Booking',
  cancel: 'Cancel',
  
  // Dashboard setup and overview
  vehiclePlanning: 'Fleet Planning & Scheduling',
  welcomeToAgency: 'Welcome to Your Rental Agency',
  addFirstVehicleMessage: 'Get started by building your vehicle fleet',
  addFirstVehicle: 'Add Your First Vehicle',
  setupStep1: 'Build Your Fleet',
  setupStep1Desc: 'Add vehicles with comprehensive details, pricing, and availability',
  setupStep2: 'Manage Customer Base',
  setupStep2Desc: 'Create and organize your customer profiles and rental history',
  setupStep3: 'Track Rental Operations',
  setupStep3Desc: 'Monitor active bookings, schedules, and rental performance',
  statisticsAnalytics: 'Business Analytics & Insights',
  
  // Agency Setup
  agencySetup: {
    setupRequired: 'Setup Required',
  },
  
  // Locations
  locations: {
    title: 'Agency Locations',
    description: 'Manage the locations where your vehicles can be picked up and returned',
    addLocation: 'Add Location',
    addNewLocation: 'Add New Location',
    editLocation: 'Edit Location',
    searchLocation: 'Search for a location',
    searchPlaceholder: 'Search airports, train stations, city centers...',
    searchHelp: 'Search for popular locations or add your own custom location',
    name: 'Location Name',
    namePlaceholder: 'Main Parking, Office, etc.',
    city: 'City',
    cityPlaceholder: 'Enter city',
    address: 'Address',
    addressPlaceholder: 'Enter address',
    setAsDefault: 'Set as default location',
    defaultLocationUpdated: 'Default location updated successfully',
    locationDeleted: 'Location deleted successfully',
    deleteConfirmTitle: 'Delete Location',
    deleteConfirmMessage: 'Are you sure you want to delete the location',
    deleteConfirmWarning: 'This action cannot be undone.',
    noLocations: 'No locations configured',
    noLocationsDesc: 'Add at least one location to start managing your vehicles',
    addFirstLocation: 'Add first location',
    default: 'Main',
    selectLocation: 'Select location',
    noLocationsAvailable: 'No locations available',
    pickupLocation: 'Pickup Location',
    returnLocation: 'Return Location',
    selectPickupLocation: 'Select pickup location',
    selectReturnLocation: 'Select return location',
    selectPickupLocationError: 'Please select a pickup location',
    selectReturnLocationError: 'Please select a return location',
  },

  // Vehicle Management
  vehicles: {
    title: 'Vehicles',
    subtitle: 'Manage your fleet of vehicles',
    addVehicle: 'Add Vehicle',
    editVehicle: 'Edit Vehicle',
    addFirstVehicle: 'Add Your First Vehicle',
    noVehicles: 'No Vehicles Found',
    noVehiclesDesc: 'Start by adding vehicles to your fleet.',
    totalVehicles: '{{count}} vehicles found',
    searchPlaceholder: 'Search vehicles...',
    
    // Vehicle fields
    make: 'Make',
    model: 'Model',
    year: 'Year',
    color: 'Color',
    seats: 'Seats',
    transmission: 'Transmission',
    fuelType: 'Fuel Type',
    pricePerDay: 'Price per Day',
    description: 'Description',
    category: 'Category',
    selectCategory: 'Select Category',
    location: 'Vehicle Location',
    selectLocation: 'Select a location',
    locationRequired: 'Please select a location',
    primaryLocationRequired: 'Every car must have at least one primary agency location',
    registrationNumber: 'Registration Number',
    currentMileage: 'Current Mileage',
    insuranceExpiryDate: 'Insurance Expiry Date',
    technicalControlExpiryDate: 'Technical Control Expiry Date',
    circulationPermitExpiryDate: 'Circulation Permit Expiry Date',
    lastOilChangeDate: 'Last Oil Change Date',
    lastOilChangeMileage: 'Last Oil Change Mileage',
    oilChangeIntervalKm: 'Oil Change Interval (km)',
    maintenanceNotes: 'Maintenance Notes',
    features: 'Features',
    images: 'Images',
    
    // Placeholders
    enterMake: 'Enter vehicle make',
    enterModel: 'Enter vehicle model',
    enterColor: 'Enter vehicle color',
    enterDescription: 'Enter vehicle description',
    enterRegistrationNumber: 'Enter registration number',
    enterMaintenanceNotes: 'Enter maintenance notes',
    
    // Actions
    edit: 'Edit',
    delete: 'Delete',
    deleteAll: 'Delete All',
    confirmDelete: 'Are you sure you want to delete this vehicle?',
    deleteConfirmTitle: 'Delete Vehicle',
    deleteConfirmMessage: 'Are you sure you want to delete this vehicle? This action cannot be undone.',
    deleteAllConfirmTitle: 'Delete Selected Vehicles',
    deleteAllConfirmMessage: 'Are you sure you want to delete {{count}} selected vehicles? This action cannot be undone.',
    cancel: 'Cancel',
    
    // Status (includes both vehicle and booking statuses for compatibility)
    status: {
      available: 'Available',
      rented: 'Rented',
      maintenance: 'Maintenance',
      pending: 'Pending',
      confirmed: 'Confirmed',
      completed: 'Completed',
      cancelled: 'Cancelled'
    },
    
    // Transmission types
    transmissionType: {
      automatic: 'Automatic',
      manual: 'Manual',
    },
    
    // Fuel types
    fuelTypes: {
      gasoline: 'Gasoline',
      diesel: 'Diesel',
      hybrid: 'Hybrid',
      electric: 'Electric',
    },
    
    // Categories
    categories: {
      economy: 'Economy',
      compact: 'Compact',
      midsize: 'Midsize',
      fullsize: 'Full Size',
      luxury: 'Luxury',
      suv: 'SUV',
    },
    
    // Image upload
    uploadImages: 'Upload Images',
    maxImages: 'Maximum 6 images',
    dragDropImages: 'Drag & drop images here or click to select',
    dragToReorder: 'Drag images to reorder them. The first image will be the main photo.',
    removeImage: 'Remove image',
    
    // Features
    addFeature: 'Add Feature',
    removeFeature: 'Remove feature',
    enterFeature: 'Enter feature name',
    suggestedFeatures: 'Suggested Features',
    addAllFeatures: 'Add All',
    removeAllFeatures: 'Remove All',
    
    // Validation messages
    makeRequired: 'Make is required',
    modelRequired: 'Model is required',
    yearRequired: 'Year is required',
    colorRequired: 'Color is required',
    categoryRequired: 'Category is required',
    transmissionRequired: 'Transmission is required',
    fuelTypeRequired: 'Fuel type is required',
    priceRequired: 'Price per day is required',
    seatsRequired: 'Number of seats is required',
    
    // Success messages
    vehicleAdded: 'Vehicle added successfully',
    vehicleUpdated: 'Vehicle updated successfully',
    vehicleDeleted: 'Vehicle deleted successfully',
    vehiclesDeleted: '{{count}} vehicles deleted successfully',
    
    // Error messages
    failedToAdd: 'Failed to add vehicle. Please try again.',
    failedToUpdate: 'Failed to update vehicle. Please try again.',
    failedToDelete: 'Failed to delete vehicle. Please try again.',
    failedToUpload: 'Failed to upload image. Please try again.',
    
    // Bulk actions
    bulkActions: 'Bulk Actions',
    selectAll: 'Select All',
    selectedCount: '{{count}} selected',
    markAsAvailable: 'Mark as Available',
    markAsRented: 'Mark as Rented',
    markAsMaintenance: 'Mark as Maintenance',
    exportSelected: 'Export Selected',
    
    // Table columns
    image: 'Image',
    availability: 'Availability',
    actions: 'Actions',
    createdAt: 'Created',
    updatedAt: 'Updated',
    
    // Form sections
    basicInformation: 'Basic Information',
    specifications: 'Specifications',
    pricing: 'Pricing',
    maintenanceInfo: 'Maintenance Information',
    additionalFeatures: 'Additional Features',
    
    // Clear form
    clearForm: 'Clear Form',
    clearFormConfirm: 'Are you sure you want to clear all form data?',
    
    // Additional validation messages
    yearInvalid: 'Year must be between 1900 and 2030',
    seatsInvalid: 'Number of seats must be between 1 and 15',
    registrationInvalid: 'Registration number must contain more than 3 characters',
    mileageInvalid: 'Mileage cannot be negative',
    oilChangeMileageInvalid: 'Oil change mileage cannot be negative',
    oilChangeIntervalInvalid: 'Oil change interval must be at least 1000 km',
    oilChangeDateInvalid: 'Last oil change date cannot be in the future',
    
    // Error messages
    permissionDenied: 'Permission denied. Make sure you have access to vehicle management for this agency.',
    invalidData: 'Invalid data provided. Please check all required fields.',
    networkError: 'Network error. Please check your internet connection and try again.',
    
    // Search placeholders
    searchBrand: 'Type to search for a brand...',
    selectModel: 'Select a model...',
    selectBrandFirst: 'Select a brand first',
    searchColor: 'Type to search for a color...',
    
    // Vehicle features
    featuresList: {
      airConditioning: 'Air Conditioning',
      powerSteering: 'Power Steering',
      centralLocking: 'Central Locking',
      electricWindows: 'Electric Windows',
      radio: 'Radio',
      gpsNavigation: 'GPS/Navigation',
      bluetooth: 'Bluetooth',
      backupCamera: 'Backup Camera',
      parkingSensors: 'Parking Sensors',
      heatedSeats: 'Heated Seats',
      sunroof: 'Sunroof',
      alloyWheels: 'Alloy Wheels',
      ledHeadlights: 'LED Headlights',
      cruiseControl: 'Cruise Control',
      premiumAudio: 'Premium Audio System',
      usbAux: 'USB/AUX',
      keylessStart: 'Keyless Start',
      sideAirbags: 'Side Airbags',
      abs: 'ABS',
      esp: 'ESP',
      brakeAssist: 'Brake Assist',
      blindSpotDetection: 'Blind Spot Detection',
      laneAssist: 'Lane Departure Warning',
      heatedWindshield: 'Heated Windshield',
      electricMirrors: 'Electric Mirrors',
    },
  },

  // Client Management
  clients: {
    title: 'Client Management',
    subtitle: 'Manage your customers and their rental history',
    addCustomer: 'Add Customer',
    addNewCustomer: 'Add New Customer',
    noClients: 'No Clients Found',
    noClientsDesc: 'Clients will appear here when they make bookings.',
    totalClients: '{{count}} client(s) found',
    searchPlaceholder: 'Search by name, email, phone or CIN...',
    selectedClients: '{{count}} selected',
    
    // Table headers
    client: 'Client',
    phone: 'Phone',
    type: 'Type',
    bookings: 'Bookings',
    spent: 'Spent',
    actions: 'Actions',
    cin: 'CIN',
    viewCinImage: 'View CIN Image',
    
    // Client types
    individual: 'Individual',
    company: 'Company',
    personalCustomerAccount: 'Personal customer account',
    businessCustomerAccount: 'Business customer account',
    
    // Add customer modal
    selectAccountType: 'Select Account Type',
    chooseAccountTypeDesc: 'Choose the type of customer account you want to create',
    stepOfTwo: 'Step {{step}} of 2',
    requiresCIN: 'Requires: CIN, Driving Permit',
    requiresICE: 'Requires: ICE Number',
    
    // Form fields
    fullName: 'Full Name',
    companyName: 'Company Name',
    phoneNumber: 'Phone Number',
    identityCardNumber: 'Identity Card Number (CIN)',
    drivingPermitNumber: 'Driving Permit Number',
    iceNumber: 'ICE Number',
    clientType: 'Client Type',
    
    // Placeholders
    enterFullName: 'Enter customer\'s full name',
    enterCompanyName: 'Enter company name',
    enterPhone: 'Enter customer\'s phone',
    enterCompanyPhone: 'Enter company phone',
    enterCIN: 'Enter CIN number',
    enterPassportNumber: 'Enter passport number',
    enterResidencePermitNumber: 'Enter residence permit number',
    enterDrivingPermitNumber: 'Enter driving permit number (optional)',
    filterByRegistrationPeriod: 'Filter by registration period',
    passport: 'Passport',
    enterPassport: 'Enter passport number',
    enterResidencePermit: 'Enter residence permit number',
    enterDrivingPermit: 'Enter driving permit number',
    enterICE: 'Enter ICE number (15 digits)',
    
    // Client details modal
    clientDetails: 'Client Details',
    personalInformation: 'Personal Information',
    joinedDate: 'Joined Date',
    totalBookings: 'Total Bookings',
    totalSpent: 'Total Spent',
    averageRating: 'Average Rating',
    recentBookings: 'Recent Bookings',
    clientDocument: 'Client Document',
    identityDocument: 'Identity Document',
    downloadPDF: 'Download Contract + Identity Doc',
    noImage: 'No image',
    noIdentityDocument: 'No identity document available',
    noBookings: 'No bookings yet',
    
    // Edit client
    editClient: 'Edit Client',
    
    // Buttons and actions
    cancel: 'Cancel',
    save: 'Save',
    update: 'Update',
    viewDetails: 'View Details',
    
    // Status messages
    customerAdded: 'Customer added successfully',
    customerUpdated: 'Customer updated successfully',
    failedToAdd: 'Failed to add customer. Please try again.',
    failedToUpdate: 'Failed to update customer. Please try again.',
    
    // Validation messages
    fullNameRequired: 'Full name is required',
    phoneRequired: 'Phone number is required',
    clientTypeRequired: 'Client type is required',
    cinRequired: 'CIN is required for individuals',
    drivingPermitRequired: 'Driving permit number is required for individuals',
    iceRequired: 'ICE number is required for companies',
    iceInvalid: 'ICE number must be exactly 15 digits',
    
    // Identity document translations
    carteSejourShort: 'Carte Séjour',
    selectOneIdentityDocument: 'Please select only one identity document',
  },

  // Bookings
  bookings: {
    title: 'Booking Management',
    subtitle: 'Manage all agency bookings and track their performance',
    searchBookings: 'Search bookings',
    periodLabel: 'Period',
    filterByPeriodPlaceholder: 'Filter by period...',
    allPeriod: 'All Periods',
    activeFilters: 'Active Filters',
    clearAllFilters: 'Clear All Filters',
    searchLabel: 'Search',
    statusLabel: 'Booking Status',
    vehicleLabel: 'Vehicle',
    noBookingsFound: 'No matching bookings',
    noBookings: 'No bookings',
    tryDifferentFilter: 'Try using different filters or clearing current filters.',
    noBookingsDesc: 'No bookings have been created yet. Start by creating your first booking.',
    createFirstBooking: 'Create First Booking',
    totalBookings: '{{count}} booking(s) found',
    bookingDetails: 'Booking Details',
    clientInformation: 'Client Information',
    vehicleInformation: 'Vehicle Information',
    rentalPeriod: 'Rental Period',
    pricing: 'Pricing',
    startDate: 'Start Date',
    endDate: 'End Date',
    extensible: 'Extensible',
    startDateHeader: 'Start Date',
    endDateHeader: 'End Date',
    pickupTime: 'Pickup Time',
    returnTime: 'Return Time',
    pickupTimeHeader: 'Pickup Time',
    returnTimeHeader: 'Return Time',
    totalPrice: 'Total Price',
    deposit: 'Deposit',
    duration: 'Duration',
    drivingLicense: 'Driving License',
    contract: 'Contract',
    bookingStatus: 'Booking Status',
    invoiceStatus: 'Invoice Status',
    contractActions: 'Contract Actions',
    downloadInvoice: 'Download Invoice',
    delete: 'Delete',
    cancel: 'Cancel',
    edit: 'Edit',
    actions: 'Actions',
    confirmDelete: 'Are you sure you want to delete this booking?',
    deleteConfirmTitle: 'Delete Booking',
    deleteConfirmMessage: 'Are you sure you want to delete this booking? This action cannot be undone.',
    deleteSuccess: 'Booking deleted successfully',
    deleteError: 'Error deleting booking',
    createSuccess: 'Booking created successfully',
    updateSuccess: 'Booking updated successfully',
    saveError: 'Error saving booking',
    days: 'days',
    customer: 'Customer',
    driver: 'Driver',
    vehicle: 'Vehicle',
    dates: 'Dates',
    pickupLocation: 'Pickup Location',
    returnLocation: 'Return Location',
    allStatuses: 'All Statuses',
    selectVehiclePlaceholder: 'Select vehicle...',
    columns: 'Columns',
    customizeColumns: 'Customize Columns',
    resetColumns: 'Reset Columns',
    bookingInformation: 'Booking Information',
    financialInformation: 'Financial Information',
    additionalInformation: 'Additional Information',
    depositAmount: 'Deposit Amount',
    bankGuarantee: 'Bank Guarantee / Caution',
    damageAmount: 'Damage Amount',
    notes: 'Notes',
    createdAt: 'Created At',
    status: {
      pending: 'Pending',
      confirmed: 'Confirmed',
      completed: 'Completed',
      cancelled: 'Cancelled'
    },
    
    // Booking modal translations
    searchCustomer: 'Search by name or CIN...',
    createNewCustomer: 'Create New Customer',
    individual: 'Individual',
    company: 'Company',
    noCustomerFound: 'No customers found',
    noCustomersYet: 'No customers have been added yet',
    addFirstCustomer: 'Add First Customer',
    selectVehicle: 'Select Vehicle',
    selectDatePlaceholder: 'Select date',
    timePlaceholder: 'Select time',
    selectClientError: 'Please select a customer',
    selectVehicleError: 'Please select a vehicle',
    selectDatesError: 'Please select dates',
    selectDateRange: 'Select a date range to calculate the price',
    totalPriceError: 'Total price must be greater than 0',
    
    // Secondary driver translations
    secondaryDriver: 'Secondary Driver',
    optional: 'Optional',
    noSecondaryDriver: 'No secondary driver',
    searchSecondaryDriver: 'Search for secondary driver...',
    addSecondaryDriver: 'Add Secondary Driver',
    createNewSecondaryDriver: 'Create New Secondary Driver',
    clearSecondaryDriver: 'Clear',
    noCustomersForSecondaryDriver: 'No customers available for secondary driver',
    noSecondaryDriverFound: 'No secondary driver found',
    secondaryDriverAdded: 'Secondary driver added successfully',
    errorAddingSecondaryDriver: 'Error adding secondary driver',
    
    // Payment status for new bookings
    paymentStatusDescription: 'Choose payment status for the new booking - will be applied to invoice automatically',
    paymentOptions: {
      paid: 'Fully Paid',
      partial_paid: 'Partially Paid'  
    },

    // Additional keys for booking modal
    selectCarFirst: 'Select a vehicle first',
    selectDateFirst: 'Select a date first',
    locationsAvailable: 'locations available',
    carNoLocation: 'This vehicle has no assigned location',
    save: 'Save',
    createBooking: 'Create Booking',
    
    // Booking details and form labels
    pickupTimeLabel: 'Pickup Time',
    returnTimeLabel: 'Return Time',
    pickupLocationLabel: 'Pickup Location',
    returnLocationLabel: 'Return Location',
    pickupLocationPlaceholder: 'Pickup location',
    returnLocationPlaceholder: 'Return location',
    totalPricePlaceholder: 'Total price',
    depositAmountPlaceholder: 'Deposit amount',
    pricePerDay: 'Price per day',
    notesPlaceholder: 'Add notes...',
    noNotes: 'No notes',
    agency: 'Agency',
    notSpecified: 'Not specified',
    paid: 'Paid',
    pending: 'Pending',
    failed: 'Failed',
    refunded: 'Refunded',
    
    // Customer management in booking modal
    addNewCustomer: 'Add New Customer',
    selectAccountType: 'Select Account Type',
    chooseAccountTypeDesc: 'Choose the type of customer account you want to create',
    personalCustomerAccount: 'Personal customer account',
    businessCustomerAccount: 'Business customer account',
    fullName: 'Full Name',
    phoneNumber: 'Phone Number',
    identityCardNumber: 'Identity Card Number (CIN)',
    residencePermit: 'Residence Permit (Permis de Séjour)',
    passportNumber: 'Passport Number',
    drivingLicenseNumber: 'Driving License Number',
    iceNumber: 'ICE Number',
    back: 'Back',
    stepOf: 'Step {{current}} of {{total}}',
    enterCompanyName: 'Enter company name',
    enterClientFullName: 'Enter client full name',
    enterCompanyPhone: 'Enter company phone',
    enterClientPhone: 'Enter client phone',
    enterCinNumber: 'Enter CIN number',
    enterDrivingPermitNumber: 'Enter driving permit number',
    enterIceNumber: '15 digits',
    adding: 'Adding...',
    addClient: 'Add Client',
    
    // Error messages
    cannotSelectDateRange: 'Cannot select this date range as it contains days already booked for this vehicle.',
    selectPickupLocationError: 'Please select a pickup location',
    selectReturnLocationError: 'Please select a return location',
    carAvailableFrom: 'The car will be available from',
    statusUpdated: 'Status updated successfully',
    completedBookingTooltip: 'Completed bookings cannot be modified',
    
    // Read-only mode
    readOnlyInEditMode: 'Read-only in edit mode',
  },

  // Dashboard charts and stats
  charts: {
    available: 'Available',
    rented: 'Rented',
    vehicleStats: 'Vehicle Statistics',
    availableVehicles: 'Available vehicles',
    pendingReservations: 'Pending reservations'
  },

  // Contracts integration in bookings
  contracts: {
    generateContract: 'Generate Contract',
    viewContract: 'View Contract',
    downloadContract: 'Download Contract',
    contractGenerated: 'Contract Generated',
    noContract: 'No Contract',
    contractNumber: 'Contract #{{number}}',
    generatedOn: 'Generated on {date}',
    contractExists: 'Contract already exists for this booking',
    generateNewContract: 'Generate New Contract',
    regenerateContract: 'Regenerate Contract',
    contractGeneratedSuccessfully: 'Contract generated successfully',
    failedToGenerateContract: 'Failed to generate contract',
    termsAndConditions: 'Terms and Conditions',
    defaultTerms: `1. The renter must present a valid driving license and passport/ID card.
2. The vehicle must be returned in the same condition as received.
3. Any damage to the vehicle will be charged to the renter.
4. The renter is responsible for all traffic violations during the rental period.
5. Smoking is strictly prohibited in the vehicle.
6. The vehicle must be returned with the same fuel level as provided.
7. Late return will incur additional charges.
8. The security deposit will be refunded upon satisfactory return of the vehicle.`,
    rentalAgreement: 'Rental Agreement',
    contractPreview: 'Contract Preview',
    clientSignature: 'Client Signature',
    agencySignature: 'Agency Signature',
    signedContract: 'Signed',
    pendingSignature: 'Pending',
    totalAmount: 'Total Amount',
    depositAmount: 'Deposit Amount',
    rentalPeriod: 'Rental Period',
    vehicleInformation: 'Vehicle Information',
    clientInformation: 'Client Information',
    contractStatus: 'Contract Status',
    signatureDate: 'Signature Date',
    contractActions: 'Contract Actions',
    contractDetails: 'Contract Details',
    printContract: 'Print Contract',
    emailContract: 'Email Contract',
    contractSent: 'Contract sent successfully',
    contractNotFound: 'Contract not found',
    contractPreviewTitle: 'Contract Preview - {contractNumber}',
    contractInformation: 'Contract Information',
    createdAt: 'Created on',
    template: 'Template',
    enterTerms: 'Enter terms and conditions...',
    downloadPdf: 'Download PDF',
    print: 'Print',
    emailToClient: 'Email to Client',
    showPreview: 'Show Preview',
    hidePreview: 'Hide Preview',
  },

  invoices: {
    title: 'Invoices',
    invoice: 'Invoice',
    invoiceGenerated: 'Invoice generated successfully',
    invoiceExists: 'Invoice already exists for this booking',
    invoiceError: 'Error generating invoice',
    statusUpdateError: 'Error updating invoice status',
    generateInvoice: 'Generate Invoice',
    markAsPaid: 'Mark as Paid',
    markAsSent: 'Mark as Sent',
    selectBooking: 'Select a booking',
    noBookingsAvailable: 'No bookings available',
    searchPlaceholder: 'Search by number, customer...',
    status: {
      draft: 'Draft',
      paid: 'Paid',
      partial_paid: 'Partial Paid',
      canceled: 'Canceled',
      sent: 'Sent',
      overdue: 'Overdue'
    },
    paidInvoiceProtection: 'Paid invoices cannot be modified',
    lockedInvoiceProtection: 'Paid and canceled invoices cannot be modified',
    invoiceLockedTooltip: 'Paid and canceled invoices cannot be modified'
  },

  // Dashboard specific translations (moved from dashboard namespace)
  noCarsYet: 'No vehicles yet',
  noCarsYetDesc: 'You haven\'t added any vehicles to your fleet yet. Add your first vehicle to start managing your rental business.',
  detailedAnalysis: 'Detailed analysis of your performance and key indicators',
  vehicleStatus: 'Vehicle Status',
  categoryPerformance: 'Category Performance',
  
  // Dashboard cards and chart labels
  reservations: 'Reservations',
  revenue: 'Revenue',
  revenueEvolution: 'Revenue Evolution (6 months)',
  lastReservations: 'Latest Reservations',
  revenueMad: 'Revenue (MAD)',
  monthlyRevenue: 'Monthly Revenue',
  performanceIndicators: 'Performance Indicators',
  occupancyRate: 'Occupancy Rate',
  cancellationRate: 'Cancellation Rate',
  customerSatisfaction: 'Customer Satisfaction',
  activeVehicles: 'Active Vehicles',
  availableVehicles: 'Available Vehicles',
  revenueThisMonth: 'Revenue This Month',
  payments: 'Payments',
  completed: 'Completed',
  averageDuration: 'Average Duration',
  perRental: 'Per rental',
  days: 'days',
  outOf: 'out of',
  total: 'total',
  
  // Tab labels
  all: 'All',
  confirmed: 'Confirmed',
  
  // Status translations
  confirmedBookings: 'Confirmed',
  pendingBookings: 'Pending',
  completedBookings: 'Completed',
  cancelledBookings: 'Cancelled',
  
  // Messages
  noRecentReservations: 'No recent reservations',
  noConfirmedReservations: 'No confirmed reservations',
  noPendingReservations: 'No pending reservations',
  createdOn: 'Created on',
  unknownDate: 'Unknown date',
  viewAll: 'View All',
  
  // Chart configuration labels
  chartLabels: {
    vehicles: 'Vehicles',
    bookings: 'Bookings',
    revenue: 'Revenue',
    performance: 'Performance',
    maintenance: 'Maintenance',
  },
  
  // Performance metrics
  performanceMetrics: {
    occupancyRate: 'Occupancy Rate',
    customerSatisfaction: 'Customer Satisfaction',
    cancellationRate: 'Cancellation Rate',
  },
  
  // Financial labels
  averagePerBooking: 'Avg. per booking',
  
  // Edit booking modal labels (unique ones only)
  modify: 'Modify',
  saveChanges: 'Save Changes',
  customerInformation: 'Customer Information',
  bookingDetails: 'Booking Details',
  fullName: 'Full Name',
  email: 'Email',
  phone: 'Phone',
  startDate: 'Start Date',
  endDate: 'End Date',
  duration: 'Duration',
  pricePerDay: 'Price per day',
  
  // Status options for booking modal
  statusOptions: {
    pending: 'Pending',
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },

  // Root level translations needed by BookingSystem
  columns: 'Columns',
  customizeColumns: 'Customize Columns',
  resetColumns: 'Reset Columns',
  bookingInformation: 'Booking Information',
  additionalInformation: 'Additional Information',
  financialInformation: 'Financial Information',
  customer: 'Customer',
  car: 'Car',
  vehicle: 'Vehicle',
  dates: 'Dates',
  pickupLocation: 'Pickup Location',
  returnLocation: 'Return Location',
  pickupTime: 'Pickup Time',
  returnTime: 'Return Time',
  totalPrice: 'Total Price',
  contract: 'Contract',
  actions: 'Actions',
  notes: 'Notes',
  createdAt: 'Created At',
  depositAmount: 'Deposit Amount',
  damageAmount: 'Damage Amount',
  searchBookings: 'Search bookings',
  periodLabel: 'Period',
  selectVehiclePlaceholder: 'Select vehicle...',
  filterByPeriodPlaceholder: 'Filter by period...',
  allPeriod: 'All Periods',
  activePeriod: 'Active Period',
  activeFilters: 'Active Filters',
  clearAllFilters: 'Clear All Filters',
  searchLabel: 'Search',
  vehicleLabel: 'Vehicle',

  // Calendar component translations
  today: 'Today',
  todayShort: 'Today',
  thisWeek: 'This Week',
  clientsLabel: 'Clients',
  legend: 'Legend',
  confirmedShort: 'Conf',
  pendingShort: 'Pend',
  completedShort: 'Comp',
  cancelledShort: 'Canc',
  searchVehicleOrClient: 'Search vehicle or client...',
  unknownClient: 'Unknown Client',
  notAvailable: 'Not Available',
  viewCalendar: 'View Calendar',
  
  // Calendar namespace
  calendar: {
    title: 'Vehicle Planning',
  },
  
  // Common namespace for cross-component usage
  common: {
    vehicles: 'Vehicles',
  },
  
  // Reports Section
  reports: {
    title: 'Reports & Analytics',
    subtitle: 'View your agency performance metrics',
    topCarsByBookings: 'Top Cars by Bookings',
    topCarsByRevenue: 'Top Cars by Revenue',
    topClientsByPaid: 'Top Clients by Paid Amount',
    topClientsByReservations: 'Top Clients by Reservations',
    bookings: 'bookings',
    reservations: 'reservations',
    noData: 'No data available',
    allBookingsCount: 'All bookings (pending, confirmed, completed)',
    completedBookingsOnly: 'Based on completed bookings only',
    completedAndConfirmedBookings: 'Based on completed and confirmed bookings',
    filterByStatus: 'Filter by booking status:',
    showingStatusTypes: 'Showing',
    noStatusSelected: 'No status selected - please select at least one status to view data',
    pleaseSelectStatus: 'Please select at least one booking status to view data',
  },

  // Settings translations
  settings: {
    // Tab names
    general: 'General',
    locations: 'Locations',
    business: 'Business',
    notifications: 'Notifications',
    
    // Main settings
    title: 'Agency Settings',
    subtitle: 'Configure your agency profile and preferences',
    settingsDescription: 'Manage your agency settings, working hours, and notification preferences.',
    
    // Agency Information
    agencyInformation: 'Agency Information',
    agencyInformationDesc: 'Basic information about your car rental agency',
    agencyLogo: 'Agency Logo',
    logoRecommendation: 'Upload your agency logo (recommended size: 200x200px)',
    
    // Financial Settings
    financialSettings: 'Financial Settings',
    financialSettingsDesc: 'Configure your financial and business operations',
    selectCurrency: 'Select Currency',
    
    // Action buttons
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    savedSuccessfully: 'Saved Successfully',
    errorSaving: 'Error Saving',
    
    // Existing translations
    companyInformation: 'Company Information',
    companyInformationDesc: 'Basic information about your car rental agency',
    agencyName: 'Agency Name',
    agencyNamePlaceholder: 'Enter your agency name',
    agencyDescription: 'Agency Description',
    agencyDescriptionPlaceholder: 'Brief description of your agency',
    contactInformation: 'Contact Information',
    contactInformationDesc: 'How customers can reach you',
    phone: 'Phone Number',
    phonePlaceholder: 'Enter phone number',
    email: 'Email Address',
    emailPlaceholder: 'Enter email address',
    address: 'Address',
    addressPlaceholder: 'Enter your address',
    city: 'City',
    cityPlaceholder: 'Enter city',
    country: 'Country',
    countryPlaceholder: 'Enter country',
    businessSettings: 'Business Settings',
    businessSettingsDesc: 'Configure your business operations',
    currency: 'Currency',
    taxRate: 'Tax Rate (%)',
    taxRateHelp: 'Default tax rate for bookings',
    workingHours: 'Working Hours',
    workingHoursDesc: 'Set your agency operating hours',
    
    // Company Registration Information
    companyRegistration: 'Company Registration',
    companyRegistrationDesc: 'Legal company registration details',
    iceNumber: 'ICE Number',
    iceNumberPlaceholder: 'Enter ICE number (Identifiant Commun de l\'Entreprise)',
    rcNumber: 'RC Number',
    rcNumberPlaceholder: 'Enter RC number (Registre de Commerce)',
    ifNumber: 'IF Number', 
    ifNumberPlaceholder: 'Enter IF number (Identifiant Fiscal)',
    
    openAlways: 'Open 24/7',
    openAlwaysDescription: 'Your agency is open continuously, 24 hours a day and 7 days a week',
    openAllDay: 'Open All Day',
    open: 'Open',
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },
    close: 'Close',
    closed: 'Closed',
    uploadLogo: 'Upload Logo',
    uploadLogoDesc: 'Upload your agency logo (recommended size: 200x200px)',
    logoUploaded: 'Logo uploaded successfully',
    logoUploadError: 'Failed to upload logo',
    
    // New notification preferences translations
    emailSettings: 'Email Settings',
    emailSettingsDesc: 'Configure your email notification preferences',
    emailNotifications: 'Email Notifications',
    emailNotificationsDesc: 'Enable or disable all email notifications',
    notificationEmail: 'Notification Email',
    notificationEmailPlaceholder: 'Enter email for notifications',
    notificationEmailHelp: 'If empty, notifications will be sent to your agency email',
    
    bookingNotifications: 'Booking Notifications',
    bookingNotificationsDesc: 'Configure booking-related notifications',
    bookingPendingConfirmation: 'Pending Confirmation',
    bookingPendingConfirmationDesc: 'Notify when a booking is waiting for confirmation',
    bookingReturnReminder: 'Return Reminder',
    bookingReturnReminderDesc: 'Remind customers when their rental is about to end',
    bookingOverdueAlerts: 'Overdue Alerts',
    bookingOverdueAlertsDesc: 'Alert when a vehicle is not returned on time',
    
    maintenanceAlerts: 'Maintenance Alerts',
    maintenanceAlertsDesc: 'Vehicle maintenance and compliance notifications',
    maintenanceOilChange: 'Oil Change Reminders',
    maintenanceOilChangeDesc: 'Notify when vehicles need oil changes',
    maintenanceInsuranceExpiry: 'Insurance Expiry',
    maintenanceInsuranceExpiryDesc: 'Alert when vehicle insurance is about to expire',
    maintenanceTechnicalControl: 'Technical Control',
    maintenanceTechnicalControlDesc: 'Remind about mandatory technical inspections',
    
    timingSettings: 'Timing Settings',
    timingSettingsDesc: 'Configure when notifications are sent',
    pendingConfirmationRemindAfter: 'Pending Confirmation Reminder',
    pendingConfirmationTimingHelp: 'Minutes after booking creation to send reminder',
    returnReminderBefore: 'Return Reminder',
    returnReminderTimingHelp: 'Minutes before rental end to send reminder',
    overdueAlertAfter: 'Overdue Alert',
    overdueAlertTimingHelp: 'Minutes after rental end to send alert',
    minutes: 'min',
    
    saveNotificationSettings: 'Save Notification Settings',
    saveNotificationSettingsDesc: 'Apply your notification preferences',
    
    notificationPreferences: 'Notification Preferences',
    notificationPreferencesDesc: 'Configure your notification settings',
    advancedNotificationTesting: 'Advanced Notification Testing',
    notificationTestingDesc: 'Test the advanced notification system',
    testingFlowDescription: 'Test the complete notification flow including job creation, email sending, and database storage:',
    testingFeatures: {
      emailNotifications: 'Email notification delivery',
      multilingualTemplates: 'Multilingual email templates',
      jobCreation: 'Notification job creation',
      databaseStorage: 'Database storage and tracking'
    },
    testingAdvancedNotifications: 'Testing System...',
    testAdvancedNotificationSystem: 'Test Advanced Notification System',
    notificationJobsStatus: 'Notification Jobs Status',
    notificationJobsDesc: 'View and manage notification jobs',
    noNotificationJobs: 'No notification jobs yet',
    notificationJobsExplanation: 'Notification jobs will appear here when you create bookings or schedule maintenance.',
    notificationsSaved: 'Notification preferences saved successfully',
    
    // Username & URL translations
    usernameAndUrl: {
      title: 'Agency Username & URL',
      description: 'Set your custom username to get a professional subdomain like YourUsername.kiracar.com that customers can click to visit your agency',
      subdomainUrl: 'Your Subdomain URL',
      new: 'NEW',
      clickToVisit: 'Click to visit your subdomain • Redirects to your main agency page',
      copy: 'Copy',
      username: 'Username',
      usernameRequirements: '3-50 characters, letters, numbers, and hyphens only',
      usernamePlaceholder: 'your-agency-name',
      subdomainPreview: 'Your subdomain: {{slug}}.kiracar.com',
      subdomainPreviewDesc: 'Click to visit • Automatically redirects to your agency page',
      generate: 'Generate',
      suggestions: 'Suggestions:',
      saveUsername: 'Save Username',
      benefitsTitle: 'Benefits of Your Custom Subdomain',
      benefit1: 'Professional subdomain (e.g., YourAgency.kiracar.com)',
      benefit2: 'Clickable link that opens in new tab',
      benefit3: 'Easier for customers to remember and share',
      benefit4: 'Perfect for business cards and marketing materials',
      benefit5: 'Better SEO and search visibility',
      benefit6: 'Automatic redirect to your agency page',
      subdomainUrlCopied: 'Subdomain URL copied to clipboard!',
      agencyUrlCopied: 'Agency URL copied to clipboard!',
      usernameUpdatedSuccess: 'Username updated successfully!',
      usernameUpdateFailed: 'Failed to update username',
      testType: 'Test Type',
      selectTestType: 'Select test type',
      basicEmailServiceTest: 'Basic Email Service Test',
      bookingNotificationTest: 'Booking Notification Test',
      maintenanceAlertTest: 'Maintenance Alert Test',
      enableEmailToTest: 'Enable email notifications to test the system',
      testSuccessful: 'Test Successful!',
      testFailed: 'Test Failed',
      testEmailSentSuccess: 'Test email sent successfully! Message ID: {{messageId}}',
      testEmailSendFailed: 'Failed to send test email',
      testEmailError: 'An error occurred while testing email service',
    },
  },

  // Dashboard analytics  
  bookingStatus: 'Booking Status',
  
  // Missing common translations
  view: 'View',
  close: 'Close',
  loading: 'Loading...',
  error: 'Error',
  success: 'Success',
  save: 'Save',
  saving: 'Saving...',
  
  // Missing unique translations for BookingTimelineCalendar only  
  invalidFormat: 'Invalid format',
  dateError: 'Date error',
  notSpecified: 'Not specified',
  noVehicleFoundFor: 'No vehicles found for',
  noVehicleFound: 'No vehicles found',
  showing: 'Showing',
  to: 'to',
  of: 'of',
  rowsPerPage: 'Rows per page',
  previous: 'Previous',
  next: 'Next',
  idNumber: 'ID Number',
  phoneLabel: 'Phone',
  vehicleNotFound: 'Vehicle not found',
  selectStatus: 'Select status',
  selectDate: 'Select date',
  selectTime: 'Select time',
  calendarVehicles: 'Vehicles',
  calendarUnknownClient: 'Unknown client',
  calendarBookingDetails: 'Booking Details',
  calendarEdit: 'Edit',
  calendarCancel: 'Cancel',
  calendarSaving: 'Saving...',
  calendarSave: 'Save',
  calendarCustomerInformation: 'Customer Information',
  calendarVehicle: 'Vehicle',
  calendarSelectVehicle: 'Select vehicle',
  calendarStatus: 'Status',
  calendarConfirmed: 'Confirmed',
  calendarPending: 'Pending',
  calendarCompleted: 'Completed',
  calendarCancelled: 'Cancelled',
  calendarStartDate: 'Start Date',
  calendarPickupTime: 'Pickup Time',
  calendarEndDate: 'End Date',
  calendarReturnTime: 'Return Time',
  calendarPickupLocation: 'Pickup Location',
  calendarReturnLocation: 'Return Location',

  notifications: {
    title: 'Notifications',
    markAllAsRead: 'Mark all as read',
    noNotifications: 'No notifications',
    markAsReturned: 'Mark as Returned',
    editVehicle: 'Edit Vehicle',
    criticalAlerts: 'Critical Alerts',
    processing: 'Processing...',
    newNotification: 'New notification',
    priority: 'Priority',
    urgent: 'Urgent',
    important: 'Important',
    normal: 'Normal',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  },

  bookingScheduler: {
    updating: 'Updating...',
    autoSchedulerActive: 'Auto-scheduler active',
    schedulerRunning: 'Checking bookings...',
  },

  // Notification jobs translations
  notificationJobs: {
    totalJobs: 'Total Jobs',
    searchJobs: 'Search jobs...',
    allStatuses: 'All Statuses',
    allTypes: 'All Types',
    pending: 'Pending',
    executed: 'Executed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    bookingReminder: 'Booking Reminder',
    maintenanceAlert: 'Maintenance Alert',
    insuranceExpiry: 'Insurance Expiry',
    technicalControl: 'Technical Control',
    oilChange: 'Oil Change',
    noMatchingJobs: 'No matching jobs found',
    tryDifferentSearch: 'Try adjusting your search or filters',
    bookingId: 'Booking ID',
    carId: 'Car ID',
    retries: 'Retries',
    created: 'Created',
    jobData: 'Job Data',
    errorMessage: 'Error Message',
    cancelJob: 'Cancel Job',
    retryJob: 'Retry Job',
    copyJobId: 'Copy Job ID',
    jobCancelled: 'Job cancelled successfully',
    jobCancelError: 'Error cancelling job',
    jobRetried: 'Job scheduled for retry',
    jobRetryError: 'Error retrying job',
    jobIdCopied: 'Job ID copied to clipboard',
    showingJobs: 'Showing {{start}}-{{end}} of {{total}} jobs',
    status: {
      pending: 'Pending',
      executed: 'Executed',
      failed: 'Failed',
      cancelled: 'Cancelled'
    },
  },
}; 